import { useCallback, useEffect, useState } from "react";
import { api, statusConfigured, type PublicPlatformStatus } from "../lib/api";
import type { ApiState } from "./usePlatformStatus";

/**
 * Loads the PUBLIC, secret-free platform status (`GET /status`). Used by the public
 * status page, the dashboard platform-state widget, and the incident banner. Nothing
 * is persisted; a `refresh` callback allows a safe manual retry after a failure.
 */
export function usePublicStatus(): { state: ApiState<PublicPlatformStatus>; refresh: () => void } {
  const [state, setState] = useState<ApiState<PublicPlatformStatus>>(
    statusConfigured ? { kind: "loading" } : { kind: "not-configured" },
  );

  const load = useCallback(() => {
    if (!statusConfigured) {
      setState({ kind: "not-configured" });
      return () => undefined;
    }
    let active = true;
    setState({ kind: "loading" });
    api
      .publicStatus()
      .then((data) => active && setState({ kind: "ready", data }))
      .catch((error: unknown) =>
        active
          ? setState({ kind: "unavailable", detail: error instanceof Error ? error.message : undefined })
          : undefined,
      );
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => load(), [load]);

  return { state, refresh: load };
}
